import { FC, useMemo } from 'react';

import {
  AtomEffect,
  atom,
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
} from 'recoil';

import {
  simpleBugoBrandListDataAtom,
  simpleBugoBrandListStatusAtom,
} from '@shared/api/bugoBrand/bugoBrand.atom';
import { useSimpleBugoBrandListHook } from '@shared/api/bugoBrand/bugoBrand.hook';
import { BugoBrand } from '@shared/api/bugoBrand/bugoBrand.interface';
import {
  giftDeliveryCategoryListDataAtom,
  giftDeliveryCategoryListStatusAtom,
} from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.atom';
import { useGiftDeliveryCategoryListHook } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.hook';
import { GiftDeliveryCategoryData } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.interface';
import {
  giftItemListDataAtom,
  giftItemListStatusAtom,
} from '@shared/api/giftItem/giftItem.atom';
import { useGiftItemListHook } from '@shared/api/giftItem/giftItem.hook';
import { GiftItemData } from '@shared/api/giftItem/giftItem.interface';
import { GiftItemInfoType } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Image } from '@shared/api/media/media.interface';
import { PlusIcon, SearchIcon } from '@heroicons/react/outline';
import { invalidateQueriesByUrlPart } from '@shared/hooks/recoil-query';
import { useQueryClient } from 'react-query';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { Link } from 'react-router-dom';

import styles from '../components/GiftBrandManagement.module.css';

type SortType =
  | 'order'
  | 'name-asc'
  | 'name-desc'
  | 'price-asc'
  | 'price-desc'
  | 'latest'
  | 'oldest';

// 상품 유형 필터: 전체 / 쿠폰형 / 배송형
type TypeFilter = 'all' | GiftItemInfoType;

// localStorage에 필터 상태를 저장해 페이지 이동/새로고침 후에도 유지
const persistEffect =
  <T,>(key: string): AtomEffect<T> =>
  ({ setSelf, onSet }) => {
    const saved = localStorage.getItem(key);
    if (saved != null) {
      try {
        setSelf(JSON.parse(saved) as T);
      } catch {
        // 손상된 값은 무시
      }
    }
    onSet((newValue, _, isReset) => {
      if (isReset) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(newValue));
      }
    });
  };

const giftItemListBrandFilterAtom = atom<string>({
  key: 'giftItemListBrandFilterAtom',
  default: '',
  effects: [persistEffect<string>('giftItemList.brandFilter')],
});

const giftItemListSearchQueryAtom = atom<string>({
  key: 'giftItemListSearchQueryAtom',
  default: '',
  effects: [persistEffect<string>('giftItemList.searchQuery')],
});

const giftItemListSortTypeAtom = atom<SortType>({
  key: 'giftItemListSortTypeAtom',
  default: 'order',
  effects: [persistEffect<SortType>('giftItemList.sortType')],
});

const giftItemListTypeFilterAtom = atom<TypeFilter>({
  key: 'giftItemListTypeFilterAtom',
  default: 'all',
  effects: [persistEffect<TypeFilter>('giftItemList.typeFilter')],
});

const giftItemListDeliveryCategoryFilterAtom = atom<string>({
  key: 'giftItemListDeliveryCategoryFilterAtom',
  default: '',
  effects: [persistEffect<string>('giftItemList.deliveryCategoryFilter')],
});

const giftItemListSubCategoryFilterAtom = atom<string>({
  key: 'giftItemListSubCategoryFilterAtom',
  default: '',
  effects: [persistEffect<string>('giftItemList.subCategoryFilter')],
});

export const GiftItemListPage: FC = function GiftItemListPage() {
  const { sellerRole } = useAuth();
  const queryClient = useQueryClient();

  // 필터 및 검색 상태 (localStorage에 저장되어 페이지 이동 후에도 유지됨)
  const [selectedBugoBrandId, setSelectedBugoBrandId] = useRecoilState(
    giftItemListBrandFilterAtom,
  );
  const [searchQuery, setSearchQuery] = useRecoilState(giftItemListSearchQueryAtom);
  const [sortType, setSortType] = useRecoilState(giftItemListSortTypeAtom);
  const [typeFilter, setTypeFilter] = useRecoilState(giftItemListTypeFilterAtom);
  const [selectedDeliveryCategoryId, setSelectedDeliveryCategoryId] = useRecoilState(
    giftItemListDeliveryCategoryFilterAtom,
  );
  const [selectedSubCategory, setSelectedSubCategory] = useRecoilState(
    giftItemListSubCategoryFilterAtom,
  );

  // 필터 초기화
  const resetBrandFilter = useResetRecoilState(giftItemListBrandFilterAtom);
  const resetSearchQuery = useResetRecoilState(giftItemListSearchQueryAtom);
  const resetSortType = useResetRecoilState(giftItemListSortTypeAtom);
  const resetTypeFilter = useResetRecoilState(giftItemListTypeFilterAtom);
  const resetDeliveryCategoryFilter = useResetRecoilState(
    giftItemListDeliveryCategoryFilterAtom,
  );
  const resetSubCategoryFilter = useResetRecoilState(giftItemListSubCategoryFilterAtom);

  const resetAllFilters = () => {
    resetBrandFilter();
    resetSearchQuery();
    resetSortType();
    resetTypeFilter();
    resetDeliveryCategoryFilter();
    resetSubCategoryFilter();
  };

  // 기본값에서 벗어난 필터가 하나라도 있는지
  const hasActiveFilter =
    !!selectedBugoBrandId ||
    !!searchQuery ||
    sortType !== 'order' ||
    typeFilter !== 'all' ||
    !!selectedDeliveryCategoryId ||
    !!selectedSubCategory;

  // BugoBrand 목록 조회
  useSimpleBugoBrandListHook();
  const bugoBrandListStatus = useRecoilValue(simpleBugoBrandListStatusAtom);
  const bugoBrandList = useRecoilValue(simpleBugoBrandListDataAtom);

  // 배송 카테고리 목록 조회 (배송형 필터의 하위 카테고리 선택용)
  useGiftDeliveryCategoryListHook(sellerRole());
  const deliveryCategoryListStatus = useRecoilValue(giftDeliveryCategoryListStatusAtom);
  const deliveryCategoryList = useRecoilValue(giftDeliveryCategoryListDataAtom);

  // GiftItem 목록 조회
  useGiftItemListHook(sellerRole());

  const status = useRecoilValue(giftItemListStatusAtom);
  const allItemsData = useRecoilValue(giftItemListDataAtom);

  // 선택된 브랜드에 따라 프론트에서 필터링
  const rawData = useMemo(() => {
    if (!allItemsData) return null;
    if (!selectedBugoBrandId) return allItemsData;
    return allItemsData.filter((item) => {
      const brandId =
        typeof item.bugoBrand === 'object' ? item.bugoBrand._id : item.bugoBrand;
      return brandId === selectedBugoBrandId;
    });
  }, [allItemsData, selectedBugoBrandId]);

  const getImageUrl = (item: GiftItemData): string => {
    const image = item.image || item.giftItemInfo?.image;
    if (image && typeof image === 'object' && 'url' in image) {
      return fileBucketUrl((image as Image).url, 'raw');
    }
    return '';
  };

  // 원상품이 판매종료(isEnabled=false)면 이 판매상품도 사실상 비활성
  const isItemDisabled = (item: GiftItemData) => item.giftItemInfo?.isEnabled === false;

  const getName = (item: GiftItemData): string => {
    return item.name || item.giftItemInfo?.name || '';
  };

  const getBugoBrandName = (item: GiftItemData): string => {
    if (item.bugoBrand && typeof item.bugoBrand === 'object') {
      return item.bugoBrand.bugoAgency?.corpName || item.bugoBrand._id || '-';
    }
    return '-';
  };

  // 표시 순서 가져오기 (GiftItem.order 우선, 없으면 GiftItemInfo.order)
  const getDisplayOrder = (item: GiftItemData): number => {
    if (item.order !== undefined) return item.order;
    if (item.giftItemInfo?.order !== undefined) return item.giftItemInfo.order;
    return Number.MAX_SAFE_INTEGER;
  };

  // 상품 유형 (쿠폰형/배송형) 가져오기 (GiftItemInfo 기준)
  const getItemType = (item: GiftItemData): GiftItemInfoType | undefined => {
    return item.giftItemInfo?.type;
  };

  // 배송 카테고리 ID 가져오기 (GiftItem 오버라이드 우선, 없으면 GiftItemInfo)
  const getDeliveryCategoryId = (item: GiftItemData): string | undefined => {
    const override = item.giftDeliveryCategory;
    if (override) {
      return typeof override === 'object' ? override._id : override;
    }
    const infoCategory = item.giftItemInfo?.giftDeliveryCategory;
    if (infoCategory) {
      return typeof infoCategory === 'object'
        ? (infoCategory as GiftDeliveryCategoryData)._id
        : (infoCategory as unknown as string);
    }
    return undefined;
  };

  // 하위분류 가져오기 (GiftItemInfo 기준)
  const getSubCategory = (item: GiftItemData): string | undefined => {
    return item.giftItemInfo?.subCategory;
  };

  // GiftItem 가격이 원상품(GiftItemInfo) 가격과 다를 때 원상품 가격을 표기
  // emphasize=true (정가/판매가): 주의가 필요하므로 강조 표기
  // emphasize=false (도매가): 참고용이므로 작게 표기
  const renderPriceDiff = (
    override: number | undefined,
    origin: number | undefined,
    label: string,
    emphasize = false,
  ) => {
    if (origin === undefined || override === undefined || override === origin) {
      return null;
    }
    return (
      <span
        className={
          emphasize
            ? 'ml-1 rounded bg-amber-100 px-1 py-0.5 text-xs font-semibold text-amber-700'
            : 'ml-0.5 text-[10px] text-gray-400'
        }
        title={`원상품 ${label}(${origin.toLocaleString()}원)와 다릅니다`}
      >
        {emphasize ? '⚠ 원' : '원'} {origin.toLocaleString()}원
      </span>
    );
  };

  // 원상품(GiftItemInfo)과의 가격 일치 상태
  // 'full'  : 정가/판매가/도매가 모두 동일
  // 'core'  : 정가/판매가는 동일하나 도매가만 다름
  // 'none'  : 정가 또는 판매가가 다름
  const getOriginMatchLevel = (item: GiftItemData): 'full' | 'core' | 'none' => {
    const info = item.giftItemInfo;
    if (!info) return 'none';
    const coreSame =
      item.priceRegular === info.priceRegular && item.priceRetail === info.priceRetail;
    if (!coreSame) return 'none';
    return item.priceWhole === info.priceWhole ? 'full' : 'core';
  };

  // 필터링 및 정렬된 데이터
  const filteredAndSortedData = useMemo(() => {
    if (!rawData) return [];

    let result = [...rawData];

    // 1. 검색 필터
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((item) => {
        const name = getName(item).toLowerCase();
        const brandName = getBugoBrandName(item).toLowerCase();
        return name.includes(query) || brandName.includes(query);
      });
    }

    // 2. 상품 유형 필터 (쿠폰형/배송형)
    if (typeFilter !== 'all') {
      result = result.filter((item) => getItemType(item) === typeFilter);
    }

    // 3. 배송 카테고리 필터 (배송형일 때만)
    if (typeFilter === GiftItemInfoType.Delivery && selectedDeliveryCategoryId) {
      result = result.filter(
        (item) => getDeliveryCategoryId(item) === selectedDeliveryCategoryId,
      );
    }

    // 3-1. 하위분류 필터 (배송형일 때만)
    if (typeFilter === GiftItemInfoType.Delivery && selectedSubCategory) {
      result = result.filter((item) => getSubCategory(item) === selectedSubCategory);
    }

    // 4. 정렬
    switch (sortType) {
      case 'order':
        result.sort((a, b) => getDisplayOrder(a) - getDisplayOrder(b));
        break;
      case 'name-asc':
        result.sort((a, b) => getName(a).localeCompare(getName(b), 'ko'));
        break;
      case 'name-desc':
        result.sort((a, b) => getName(b).localeCompare(getName(a), 'ko'));
        break;
      case 'price-asc':
        result.sort((a, b) => (a.priceRetail || 0) - (b.priceRetail || 0));
        break;
      case 'price-desc':
        result.sort((a, b) => (b.priceRetail || 0) - (a.priceRetail || 0));
        break;
      case 'latest':
        result.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        break;
      case 'oldest':
        result.sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
        break;
    }

    return result;
  }, [
    rawData,
    searchQuery,
    sortType,
    typeFilter,
    selectedDeliveryCategoryId,
    selectedSubCategory,
  ]);

  // 통계 정보
  const stats = useMemo(() => {
    if (!rawData) return { total: 0 };
    return { total: rawData.length };
  }, [rawData]);

  // 현재 보이는 목록 중 정가/판매가가 원상품과 다른 상품 개수
  const priceDiffCount = useMemo(() => {
    return filteredAndSortedData.filter(
      (item) => getOriginMatchLevel(item) === 'none' && !!item.giftItemInfo,
    ).length;
  }, [filteredAndSortedData]);

  // 하위분류 필터 옵션 목록
  // 특정 배송 카테고리가 선택되면 해당 카테고리의 subCategories 기준,
  // 그렇지 않으면 현재 상품 목록에서 사용 중인 하위분류를 수집한다.
  const availableSubCategories = useMemo(() => {
    if (selectedDeliveryCategoryId) {
      const category = deliveryCategoryList?.find(
        (c) => c._id === selectedDeliveryCategoryId,
      );
      if (category?.subCategories?.length) {
        return [...category.subCategories];
      }
    }
    if (!rawData) return [];
    const set = new Set<string>();
    rawData.forEach((item) => {
      if (getItemType(item) === GiftItemInfoType.Delivery) {
        const sub = getSubCategory(item);
        if (sub) set.add(sub);
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ko'));
  }, [rawData, selectedDeliveryCategoryId, deliveryCategoryList]);

  const retry = () => invalidateQueriesByUrlPart(queryClient, 'gift-item');
  const cell = 'block px-5 py-2 xl:table-cell xl:py-5 xl:align-top';

  return (
    <div
      className={`${styles.page} min-h-screen bg-gray-50 px-4 py-6 text-gray-900 sm:px-6 lg:px-8 lg:py-8`}
    >
      <div className="mx-auto max-w-[1440px]">
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">상조별 상품관리</h1>
            <p className="mt-2 text-sm text-gray-500">
              상조별 판매상품과 가격, 정산 조건을 관리합니다.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/gift-seller/item/bulk"
              className="rounded border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              브랜드 일괄 등록
            </Link>
            <Link
              to="/gift-seller/item/new"
              className="inline-flex items-center gap-2 rounded bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <PlusIcon aria-hidden className="h-4 w-4" />
              상품 등록
            </Link>
          </div>
        </header>
        {status === 'success' && priceDiffCount > 0 && (
          <p className="mb-4 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            정가 또는 판매가가 원상품과 다른 상품이 {priceDiffCount}개 있습니다.
          </p>
        )}
        <section
          aria-label="상조별 상품 목록"
          className="rounded-md border border-gray-200 bg-white"
        >
          <div className="flex flex-col gap-4 px-5 pt-5 xl:flex-row xl:items-start xl:justify-between">
            <div className="relative w-full shrink-0 sm:max-w-sm xl:order-last xl:w-72">
              <SearchIcon
                aria-hidden
                className="absolute left-3 top-3 h-4 w-4 text-gray-400"
              />
              <input
                type="search"
                aria-label="상조별 상품 검색"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="상품명, 상조명 검색"
                className="h-10 w-full rounded border border-gray-300 pl-9 pr-3 text-sm"
              />
            </div>
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
              {/* 상조 필터 */}
              <select
                aria-label="상조 필터"
                value={selectedBugoBrandId}
                onChange={(e) => setSelectedBugoBrandId(e.target.value)}
                className="min-w-0 max-w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm disabled:bg-gray-50"
                disabled={bugoBrandListStatus !== 'success'}
              >
                <option value="">전체 상조</option>
                {bugoBrandListStatus === 'success' &&
                  bugoBrandList?.map((brand: BugoBrand) => (
                    <option key={brand._id} value={brand._id}>
                      {brand.bugoAgency?.corpName || brand._id}
                    </option>
                  ))}
              </select>

              {/* 상품 유형 필터 (쿠폰형/배송형) */}
              <select
                aria-label="상품 유형 필터"
                value={typeFilter}
                onChange={(e) => {
                  const value = e.target.value as TypeFilter;
                  setTypeFilter(value);
                  // 배송형이 아니면 배송 카테고리/하위분류 필터 초기화
                  if (value !== GiftItemInfoType.Delivery) {
                    setSelectedDeliveryCategoryId('');
                    setSelectedSubCategory('');
                  }
                }}
                className="min-w-0 max-w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm disabled:bg-gray-50"
              >
                <option value="all">전체 유형</option>
                <option value={GiftItemInfoType.Coupon}>쿠폰형</option>
                <option value={GiftItemInfoType.Delivery}>배송형</option>
              </select>

              {/* 배송 카테고리 필터 (배송형 선택 시에만 노출) */}
              {typeFilter === GiftItemInfoType.Delivery && (
                <select
                  aria-label="배송 카테고리 필터"
                  value={selectedDeliveryCategoryId}
                  onChange={(e) => {
                    // 배송 카테고리가 바뀌면 하위분류 필터는 초기화
                    setSelectedDeliveryCategoryId(e.target.value);
                    setSelectedSubCategory('');
                  }}
                  className="min-w-0 max-w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm disabled:bg-gray-50"
                  disabled={deliveryCategoryListStatus !== 'success'}
                >
                  <option value="">전체 배송 카테고리</option>
                  {deliveryCategoryListStatus === 'success' &&
                    deliveryCategoryList?.map((category: GiftDeliveryCategoryData) => (
                      <option key={category._id} value={category._id}>
                        {category.name}
                      </option>
                    ))}
                </select>
              )}

              {/* 하위분류 필터 (배송형 선택 시에만 노출) */}
              {typeFilter === GiftItemInfoType.Delivery && (
                <select
                  aria-label="하위분류 필터"
                  value={selectedSubCategory}
                  onChange={(e) => setSelectedSubCategory(e.target.value)}
                  className="min-w-0 max-w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm disabled:bg-gray-50"
                >
                  <option value="">전체 하위분류</option>
                  {availableSubCategories.map((sub) => (
                    <option key={sub} value={sub}>
                      {sub}
                    </option>
                  ))}
                </select>
              )}

              {/* 정렬 */}
              <select
                aria-label="상품 정렬"
                value={sortType}
                onChange={(e) => setSortType(e.target.value as SortType)}
                className="min-w-0 max-w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm disabled:bg-gray-50"
              >
                <option value="order">표시순서</option>
                <option value="latest">최신순</option>
                <option value="oldest">오래된순</option>
                <option value="name-asc">이름 ㄱ→ㅎ</option>
                <option value="name-desc">이름 ㅎ→ㄱ</option>
                <option value="price-asc">가격 낮은순</option>
                <option value="price-desc">가격 높은순</option>
              </select>

              {/* 필터 초기화 */}
              {hasActiveFilter && (
                <button
                  onClick={resetAllFilters}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100"
                >
                  필터 초기화
                </button>
              )}
            </div>
          </div>
          <p role="status" className="px-5 py-4 text-xs text-gray-500">
            {status === 'success'
              ? `총 ${filteredAndSortedData.length}개 / 선택 상조 ${stats.total}개${
                  hasActiveFilter ? ` · 전체 ${allItemsData?.length ?? 0}개` : ''
                }`
              : status === 'error'
              ? '조회 실패'
              : '불러오는 중'}
          </p>
          {status === 'error' ? (
            <div role="alert" className="border-t border-gray-200 px-5 py-16 text-center">
              <p className="font-medium">상조별 상품을 불러오지 못했습니다.</p>
              <button
                type="button"
                onClick={retry}
                className="mt-5 rounded border border-gray-300 px-4 py-2 text-sm"
              >
                다시 시도
              </button>
            </div>
          ) : status !== 'success' ? (
            <div className="flex justify-center border-t border-gray-200 py-20">
              <Loading />
            </div>
          ) : filteredAndSortedData.length === 0 ? (
            <div className="border-t border-gray-200 px-5 py-16 text-center">
              <h2 className="font-medium">
                {hasActiveFilter
                  ? '조건에 맞는 상품이 없습니다.'
                  : '첫 판매상품을 등록해보세요.'}
              </h2>
              <p className="mt-2 text-sm text-gray-500">
                {hasActiveFilter
                  ? '검색어와 상조, 상품 유형을 확인해주세요.'
                  : '원상품과 상조를 연결하고 판매 조건을 설정합니다.'}
              </p>
            </div>
          ) : (
            <table className="block w-full text-left text-sm xl:table">
              <caption className="sr-only">상조별 판매상품과 가격 및 정산 정보</caption>
              <thead className="hidden border-t border-gray-200 bg-gray-50 xl:table-header-group">
                <tr>
                  {['상품 / 원상품', '상조', '판매 가격', '정산 조건', '관리'].map(
                    (heading) => (
                      <th
                        key={heading}
                        scope="col"
                        className="px-5 py-3 text-xs font-medium text-gray-500"
                      >
                        {heading}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="block xl:table-row-group">
                {filteredAndSortedData.map((item) => (
                  <tr
                    key={item._id}
                    className="block border-t border-gray-200 py-4 hover:bg-gray-50/60 xl:table-row xl:py-0"
                  >
                    <th scope="row" className={`${cell} xl:w-[30%]`}>
                      <div className="flex items-start gap-3">
                        {getImageUrl(item) && (
                          <img
                            src={getImageUrl(item)}
                            alt=""
                            className={`h-12 w-12 shrink-0 rounded border border-gray-100 object-cover ${
                              isItemDisabled(item) ? 'opacity-60 grayscale' : ''
                            }`}
                          />
                        )}
                        <div className="min-w-0">
                          <Link
                            to={`/gift-seller/item/${item._id}/edit`}
                            className="break-words text-base font-semibold hover:text-indigo-700 hover:underline"
                          >
                            {getName(item)}
                          </Link>
                          <div className="mt-2 flex flex-wrap gap-2 text-xs font-normal text-gray-500">
                            <span>
                              {getItemType(item) === GiftItemInfoType.Delivery
                                ? '배송형'
                                : getItemType(item) === GiftItemInfoType.Coupon
                                ? '쿠폰형'
                                : '유형 미지정'}
                            </span>
                            {isItemDisabled(item) && <span>판매종료</span>}
                            {(item.order !== undefined ||
                              item.giftItemInfo?.order !== undefined) && (
                              <span>
                                순서 {getDisplayOrder(item)}
                                {item.order === undefined ? ' · 원상품 기준' : ''}
                              </span>
                            )}
                          </div>
                          {item.giftItemInfo?._id && (
                            <Link
                              to={`/gift-seller/item-info/${item.giftItemInfo._id}/edit`}
                              aria-label={`${item.giftItemInfo.name} 원상품 수정`}
                              className="mt-2 inline-block break-words text-xs font-normal text-gray-500 underline underline-offset-4 hover:text-indigo-700"
                            >
                              원상품 · {item.giftItemInfo.name} ↗
                            </Link>
                          )}
                        </div>
                      </div>
                    </th>
                    <td className={`${cell} text-gray-700`}>
                      <span className="mr-2 text-xs text-gray-500 xl:hidden">상조</span>
                      {getBugoBrandName(item)}
                    </td>
                    <td className={`${cell} space-y-1 tabular-nums`}>
                      {item.priceRegular ? (
                        <p className="text-xs text-gray-500">
                          정가 {item.priceRegular.toLocaleString()}원
                          {renderPriceDiff(
                            item.priceRegular,
                            item.giftItemInfo?.priceRegular,
                            '정가',
                            true,
                          )}
                        </p>
                      ) : null}
                      <p className="font-medium">
                        판매 {item.priceRetail?.toLocaleString()}원
                        {renderPriceDiff(
                          item.priceRetail,
                          item.giftItemInfo?.priceRetail,
                          '판매가',
                          true,
                        )}
                      </p>
                      {getOriginMatchLevel(item) !== 'none' && (
                        <p className="text-xs text-gray-500">
                          {getOriginMatchLevel(item) === 'full'
                            ? '원상품 가격 동일'
                            : '정가·판매가 동일 / 도매가 다름'}
                        </p>
                      )}
                    </td>
                    <td
                      className={`${cell} space-y-1 text-xs tabular-nums text-gray-600`}
                    >
                      <p>
                        도매 {item.priceWhole?.toLocaleString()}원
                        {renderPriceDiff(
                          item.priceWhole,
                          item.giftItemInfo?.priceWhole,
                          '도매가',
                        )}
                      </p>
                      {item.priceRetail && item.priceWhole ? (
                        <p>
                          마진 {(item.priceRetail - item.priceWhole).toLocaleString()}원 (
                          {Math.round(
                            ((item.priceRetail - item.priceWhole) / item.priceRetail) *
                              100,
                          )}
                          %)
                        </p>
                      ) : null}
                      {item.priceRebate ? (
                        <p>리베이트 {item.priceRebate.toLocaleString()}원</p>
                      ) : null}
                      <p>{item.isTaxable ? '과세' : '비과세'}</p>
                    </td>
                    <td className={cell}>
                      <Link
                        to={`/gift-seller/item/${item._id}/edit`}
                        className="inline-block whitespace-nowrap rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        수정
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </div>
  );
};
